import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, Phone, X } from "lucide-react";

export const scrollTo = (target) => {
  const element =
    typeof target === "string" ? document.querySelector(target) : target;
  if (!element) return;

  if (window.__lenis) {
    window.__lenis.scrollTo(element, {
      duration: 1.1,
      lock: true,
      force: true,
      onComplete: () => {
        if (element.id) window.history.replaceState(null, "", `#${element.id}`);
      },
    });
  } else {
    element.scrollIntoView({ behavior: "smooth" });
    if (element.id) window.history.replaceState(null, "", `#${element.id}`);
  }
};

const LINKS = [
  { label: "ABOUT", href: "#about", id: "about" },
  { label: "SERVICES", href: "#services", id: "services" },
  { label: "COVERAGE", href: "#coverage", id: "coverage" },
  { label: "CARRIERS", href: "#carrier-requirements", id: "carriers" },
  { label: "SHIPPERS", href: "#shippers", id: "shippers" },
  { label: "CONTACT", href: "#contact", id: "contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollTo(href);
  };

  return (
    <>
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-500 ${
        scrolled
          ? "border-[#222736] bg-[#0A0B0E]/80 backdrop-blur-xl"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 w-full max-w-[1600px] items-center justify-between gap-6 px-6 lg:px-10">
        <button
          data-testid="nav-brand-logo"
          onClick={() => go("#hero")}
          aria-label="Merchant Alliance Brokerage — back to top"
          className="flex shrink-0 items-center"
        >
          <img
            src="/logo-band.png"
            alt="Merchant Alliance Brokerage logo"
            className="h-12 w-auto sm:h-14"
          />
          <span className="ml-3 border-l border-[#383F56] pl-3 text-left">
            <span className="block whitespace-nowrap font-display text-[11px] font-black uppercase text-[#B9C9E6] sm:text-xs">
              Merchant Alliance
            </span>
            <span className="mt-0.5 block font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400 sm:text-[10px]">
              Brokerage
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-6 xl:flex">
          {LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={`nav-link-${link.id}`}
              onClick={() => go(link.href)}
              className="group relative whitespace-nowrap font-mono text-[11px] font-semibold tracking-[0.14em] text-slate-300 transition-colors duration-300 hover:text-white"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#FF5500] transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-3">
          <button
            data-testid="nav-request-quote-cta"
            onClick={() => go("#pathways")}
            className="hidden items-center gap-2 whitespace-nowrap bg-[#FF5500] px-4 py-3 font-mono text-[10px] font-bold tracking-[0.14em] text-white transition-colors duration-300 hover:bg-[#F59E0B] xl:inline-flex"
          >
            REQUEST A QUOTE
            <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
          <a
            data-testid="nav-phone-cta"
            href="tel:+18055125109"
            className="hidden items-center gap-2 whitespace-nowrap border border-[#4C5F84] bg-[#1A1D28] px-4 py-3 font-mono text-[10px] font-bold tracking-[0.12em] text-[#B9C9E6] transition-[background-color,border-color,color] duration-300 hover:border-[#6E87B4] hover:bg-[#222736] hover:text-white xl:inline-flex"
          >
            <Phone className="h-3.5 w-3.5" />
            +1 (805) 512-5109
          </a>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-11 w-11 items-center justify-center border border-[#383F56] text-slate-200 xl:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-[#222736] bg-[#0A0B0E]/95 backdrop-blur-xl xl:hidden"
          >
            <div className="flex flex-col px-6 py-4">
              {LINKS.map((link) => (
                <button
                  key={link.id}
                  data-testid={`nav-mobile-link-${link.id}`}
                  onClick={() => go(link.href)}
                  className="border-b border-[#1A1D28] py-4 text-left font-mono text-xs font-semibold tracking-[0.2em] text-slate-300 last:border-0 hover:text-white"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
    <button
      data-testid="mobile-request-quote-cta"
      onClick={() => go("#pathways")}
      className="fixed bottom-4 right-4 z-40 inline-flex min-h-12 items-center gap-2 bg-[#FF5500] px-5 py-3 font-mono text-[10px] font-bold tracking-[0.14em] text-white shadow-[0_12px_40px_rgba(0,0,0,0.45)] transition-colors duration-300 hover:bg-[#F59E0B] xl:hidden"
    >
      REQUEST A QUOTE
      <ArrowUpRight className="h-3.5 w-3.5" />
    </button>
    </>
  );
}
